/**
 * IL RITORNO DALLA PARTITA AL TORNEO — riga 12 del censimento del 26/09 (KB
 * `services/qlash-telegram-app/ui/2026-09-26-censimento-schermate-tornei.md`).
 *
 * A fine partita di un incontro di torneo l'unica uscita era «Torna a Backgammon», verso la lobby: per
 * tornare al torneo servivano tre tocchi (prova Back_6, 25/09). Chi entra al tavolo DALLA SCHEDA
 * D'INCONTRO (`useEntraAlTavolo`, l'unica porta da un torneo al tavolo) si porta dietro il torneo da cui
 * viene, nello state della navigazione, e a fine partita la schermata offre «Torna al torneo».
 * Destinazione: la scheda del TORNEO, non dell'incontro (tma-orcb, 01:13:56Z): li' ci sono la classifica
 * e il turno successivo, e la barra porta comunque all'incontro se resta un'azione.
 *
 * 🔴 **Validato in SCRITTURA e in LETTURA, stretto** (condizione di tma-orcb): un percorso INTERNO che
 * comincia con «/limulo/», fatto di segmenti non vuoti di caratteri sicuri — quindi niente «//», niente
 * schema («https:»), niente «\», niente «?»/«#», niente segmenti punto, NEMMENO CODIFICATI («%2e%2e»,
 * «.%2e»: per lo standard URL sono «..», e il router li risolve fuori dalla sezione — revisione di #414,
 * misurato «/limulo/%2e%2e/settings» ⇒ «/settings»), e il valore dev'essere gia' nella sua forma
 * canonica (quella che l'URL restituirebbe). Tutto il resto si IGNORA, e
 * la schermata torna al comportamento di prima. `location.state` lo scrive chiunque navighi verso la
 * rotta: senza questa porta sarebbe un redirect aperto.
 *
 * Puro: niente `import.meta.env`, niente React.
 */
import { LIMULO_ROUTE_PREFIX } from './routes.ts';

const SEGMENTO = '[A-Za-z0-9._~%-]+';
const FORMA = new RegExp(`^/${LIMULO_ROUTE_PREFIX}(?:/${SEGMENTO})+$`);
/** Un percorso della sezione e' corto: oltre, non e' un percorso nostro. */
const LUNGHEZZA_MAX = 200;

/** Il ritorno, se e' un percorso interno della sezione Limulo; altrimenti `null`. */
export function ritornoValido(valore: unknown): string | null {
  if (typeof valore !== 'string' || valore.length > LUNGHEZZA_MAX) return null;
  if (!FORMA.test(valore)) return null;
  if (valore.split('/').some((s) => /^(?:\.|%2e){1,2}$/i.test(s))) return null;
  // La forma canonica: se l'URL la riscrive (punti codificati, sequenze che normalizza), non e' nostra.
  let canonico: string;
  try {
    canonico = new URL(valore, 'https://tma.invalid').pathname;
  } catch {
    return null;
  }
  return canonico === valore ? valore : null;
}

/**
 * L'INTESTAZIONE DELLA PARTITA DI TORNEO — L-2621 (a) di Luca (30/09): «riga col torneo e il round».
 * Viaggia ACCANTO al ritorno, nello stesso state e con la stessa porta: la scrive solo «Entra al tavolo»
 * dalla scheda d'incontro, e si valida in scrittura e in lettura. Senza un ritorno valido non si mostra
 * (la riga porta al torneo: senza destinazione non c'e' riga). Vive dove vive il ritorno, in `history.state`:
 * resta a una RICARICA della stessa pagina [M chromium e webkit, 30/09], manca aprendo la partita da una
 * notifica, da un link, dalla lobby, dalla banda «riprendi» o dalla pastiglia — e allora la partita resta
 * com'e' oggi.
 * ⚠️ E' TESTO, e React lo rende come testo: la porta non difende da un'iniezione, difende la RIGA. Un nome
 * vuoto, un round che non e' un turno, un oggetto al posto di una stringa ⇒ niente riga. E i caratteri che
 * RIORDINANO il testo (bidi, revisione di #537): un U+202E capovolgerebbe il nome a schermo. Si scartano
 * quelli, uno per uno — ⛔ non tutto `\p{Cf}`, che contiene lo ZWJ delle emoji.
 */
export interface IntestazioneTorneo {
  torneo: string;
  round: number;
}
/** Oltre, non e' un nome da una riga: si torna al comportamento di oggi invece di tagliarlo a caso. */
const NOME_MAX = 120;
const ROUND_MAX = 999;
/** Controlli bidi e separatori invisibili: ALM, ZWSP, LRM/RLM, LS/PS, embedding e override, isolati, BOM. */
const RIORDINANTI = /[\u061C\u200B\u200E\u200F\u2028\u2029\u202A-\u202E\u2066-\u2069\uFEFF]/u;

/** Nome del torneo e round, se sono un nome e un turno; altrimenti `null`. */
export function intestazioneValida(valore: unknown): IntestazioneTorneo | null {
  if (typeof valore !== 'object' || valore === null) return null;
  const { torneo, round } = valore as Record<string, unknown>;
  if (typeof torneo !== 'string' || typeof round !== 'number') return null;
  const nome = torneo.trim();
  if (nome === '' || nome.length > NOME_MAX || /\p{Cc}/u.test(nome) || RIORDINANTI.test(nome)) return null;
  if (!Number.isInteger(round) || round < 1 || round > ROUND_MAX) return null;
  return { torneo: nome, round };
}

/**
 * Dalla scheda d'incontro (`/limulo/tournaments/:id/matches/:matchId`) al suo torneo
 * (`/limulo/tournaments/:id`), o `null` se il percorso non e' una scheda d'incontro.
 * ⚠️ Il segmento dell'id si riusa COM'E' (gia' codificato nell'URL): ricodificarlo lo raddoppierebbe.
 */
export function ritornoAlTorneoDi(pathname: string): string | null {
  const parti = pathname.split('/');
  if (parti.length !== 6) return null;
  const [vuoto, sezione, tornei, id, incontri, matchId] = parti;
  if (vuoto !== '' || sezione !== LIMULO_ROUTE_PREFIX || tornei !== 'tournaments') return null;
  if (incontri !== 'matches' || !id || !matchId) return null;
  return ritornoValido(`/${LIMULO_ROUTE_PREFIX}/tournaments/${id}`);
}
